import React, { MouseEvent, useMemo, useState } from 'react';
import Dropdown, { IOption, PopoverPlacement } from '../Dropdown';
import styled from 'styled-components/macro';
import { ArrowDownIcon, FormsOutlineIcon } from 'assets/icons';
import Tooltip from '../Tooltip';
import {
  FormTemplateType,
  MessageComposerFormTemplateFragment,
  ChannelMessageAttachmentType,
  useGetMessageComposerFormTemplatesLazyQuery,
  ChannelMessageAttachmentEntityType,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import sortBy from 'lodash-es/sortBy';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { MessageTemplateAttachment } from 'pages/MessageTemplates/MessageTemplateView';
import { NotificationCounter } from '@televet/kibble-ui/build/components/NotificationCounter';

interface IFormButtonProps {
  messageTemplateFormAttachments: MessageTemplateAttachment[];
  setMessageTemplateAttachments: (attachments: MessageTemplateAttachment[]) => void;
}

const FormsButton = ({
  messageTemplateFormAttachments,
  setMessageTemplateAttachments,
}: IFormButtonProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [formTemplates, setFormTemplates] = useState<IOption[]>([]);
  const formAttachmentsIds = useMemo(
    () => messageTemplateFormAttachments.map((attachment) => attachment?.entityId),
    [messageTemplateFormAttachments],
  );
  const [getFormTemplates, { loading: isLoadingFormTemplates }] = useGetMessageComposerFormTemplatesLazyQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId || '' },
        isDeleted: { equals: false },
        isActive: { equals: true },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onCompleted: (data) => {
      if (data.findManyFormTemplate) {
        setFormTemplates(
          sortBy(
            data.findManyFormTemplate
              .filter(
                ({ formTemplateType }: MessageComposerFormTemplateFragment) =>
                  formTemplateType !== FormTemplateType.WidgetRequest,
              )
              .map((formTemplate: MessageComposerFormTemplateFragment) => {
                const { title, id } = formTemplate;
                return {
                  text: title,
                  value: {
                    attachmentType: ChannelMessageAttachmentType.FormRequest,
                    entityType: ChannelMessageAttachmentEntityType.Form,
                    entityId: id,
                  },
                  isSelected: formAttachmentsIds.includes(id),
                };
              }),
            (form) => form.text.toLowerCase(),
          ),
        );
      }
    },
  });

  const handleLoadFormTemplatesDropdownOptions = (): void => {
    if (!formTemplates.length) {
      getFormTemplates();
    }
  };

  const handleFormSelect = (e: MouseEvent, option: IOption): void => {
    const updatedOptions = formTemplates.map((formTemplate) => {
      if (formTemplate.value.entityId === option.value.entityId) {
        return { ...formTemplate, isSelected: !formTemplate.isSelected };
      }

      return formTemplate;
    });
    setFormTemplates(updatedOptions);

    const { entityId } = option.value;
    if (formAttachmentsIds.includes(entityId)) {
      const updatedAttachments = messageTemplateFormAttachments.filter(
        (attachment) => attachment.entityId !== entityId,
      );
      setMessageTemplateAttachments(updatedAttachments);
    } else {
      const { attachmentType, entityType } = option.value;
      setMessageTemplateAttachments([...messageTemplateFormAttachments, { entityId, attachmentType, entityType }]);
    }
  };

  return (
    <Dropdown
      options={formTemplates}
      placement={PopoverPlacement.BottomEnd}
      onSelect={handleFormSelect}
      isMultiSelect={true}
      isLazy={true}
      closeOnClick={false}
      loadingOptions={isLoadingFormTemplates}
      onLoadOptions={handleLoadFormTemplatesDropdownOptions}
    >
      <ButtonWrapper>
        {!!messageTemplateFormAttachments.length && (
          <BadgeContainer>
            <NotificationCounter count={messageTemplateFormAttachments.length} size="sm" />
          </BadgeContainer>
        )}
        <Tooltip content="Attach form(s)" delay={[300, 0]}>
          <StyledFormIcon />
        </Tooltip>
        <StyledArrowDownIcon />
      </ButtonWrapper>
    </Dropdown>
  );
};

const ButtonWrapper = styled.div`
  align-items: center;
  border: 1px solid #d2d2d2;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  margin-left: 10px;
  padding: 5px;
  position: relative;
  width: 45px;
`;

const StyledFormIcon = styled(FormsOutlineIcon)`
  height: 20px;
  width: 20px;

  * {
    fill: #878890;
  }
`;

const StyledArrowDownIcon = styled(ArrowDownIcon)`
  height: 10px;
  width: 10px;

  * {
    fill: #acabab;
  }
`;

const BadgeContainer = styled.span`
  position: absolute;
  right: 13px;
  top: 0;
`;

export default FormsButton;
